import { CheckCircle2, Download, Loader2, RotateCw, Sparkles, UserRoundPlus, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  HANDLE_RE,
  normalizeHandle,
  useAvatarPrompt,
  useHandleAlternatives,
  useHandleStatus,
  useSuggestChannel,
  type ChannelSuggestion,
  type HandleCheck,
  type HandleStatus,
} from '@/api/endpoints/channelCreator'
import { useGenerateImage } from '@/api/endpoints/imageGeneration'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import { cn } from '@/lib/cn'
import { errorKind, errorMessage } from '@/lib/errors'
import styles from './ChannelAssist.module.scss'

/**
 * The AI half of the "Novo canal" dialog — what used to be the Channel
 * Creator tool. Three pieces, placed by the dialog around its own fields:
 *
 * - {@link SuggestPanel}: description → 3 name/handle cards; a click fills
 *   the dialog's Nome and Channel tag, which stay editable.
 * - {@link HandleStatusLine}: under the tag, says whether the @ is free on
 *   YouTube and offers free alternatives when it isn't.
 * - {@link AvatarPanel}: the profile photo from the description. Editing
 *   the description and hitting "Regenerar foto" rewrites only the avatar
 *   brief — name and tag are left alone.
 */

/** The avatar brief and the description it was written from. */
export interface AvatarBrief {
  prompt: string
  description: string
}

const MIN_DESCRIPTION = 10

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(value)
    }, ms)
    return () => {
      clearTimeout(t)
    }
  }, [value, ms])
  return debounced
}

function StatusBadge({ status }: { status: HandleStatus }) {
  if (status === 'available') {
    return (
      <span className={cn(styles.badge, styles.badgeOk)}>
        <CheckCircle2 aria-hidden /> livre
      </span>
    )
  }
  if (status === 'taken') {
    return (
      <span className={cn(styles.badge, styles.badgeBad)}>
        <XCircle aria-hidden /> ocupado
      </span>
    )
  }
  return null
}

// ---------------------------------------------------------------------------
// Description + suggestions
// ---------------------------------------------------------------------------

interface SuggestPanelProps {
  platform: 'youtube' | 'tiktok'
  description: string
  onDescriptionChange: (value: string) => void
  onPick: (suggestion: ChannelSuggestion) => void
  onBrief: (brief: AvatarBrief) => void
  /** Handle currently in the tag field, normalized — marks the picked card. */
  currentHandle: string
}

export function SuggestPanel({
  platform,
  description,
  onDescriptionChange,
  onPick,
  onBrief,
  currentHandle,
}: SuggestPanelProps) {
  const suggest = useSuggestChannel()
  const suggestions = suggest.data?.suggestions ?? []
  const trimmed = description.trim()

  function run() {
    if (trimmed.length < MIN_DESCRIPTION || suggest.isPending) return
    suggest.mutate(
      { description: trimmed, platform },
      {
        onSuccess: (data) => {
          onBrief({ prompt: data.avatar_prompt, description: trimmed })
          // Fill the fields with the top pick, so one click gets a usable
          // dialog; the cards stay there to switch.
          const first = data.suggestions.find((s) => s.handle_status !== 'taken') ?? data.suggestions[0]
          if (first) onPick(first)
        },
      },
    )
  }

  return (
    <div className={styles.panel}>
      <label className={styles.label} htmlFor="ch-description">
        Descrição do canal <span className={styles.hint}>· a IA sugere nome, @ e foto</span>
      </label>
      <Textarea
        id="ch-description"
        rows={3}
        placeholder="Tema, público, tom — e, se quiser, a direção da foto (cores, mascote, estilo)."
        value={description}
        maxLength={2000}
        onChange={(e) => {
          onDescriptionChange(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            run()
          }
        }}
      />
      <div className={styles.row}>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          leadingIcon={suggest.isPending ? <Loader2 className={styles.spin} /> : <Sparkles />}
          onClick={run}
          disabled={trimmed.length < MIN_DESCRIPTION || suggest.isPending}
        >
          {suggest.isPending
            ? 'Pensando e conferindo os @…'
            : suggestions.length
              ? 'Sugerir de novo'
              : 'Sugerir nome e @'}
        </Button>
        {suggest.isError ? (
          <span className={styles.error}>{errorMessage(suggest.error, 'Falha ao gerar sugestões.')}</span>
        ) : null}
      </div>

      {suggestions.length > 0 ? (
        <div className={styles.cards}>
          {suggestions.map((s) => {
            const active = normalizeHandle(currentHandle) === s.handle
            return (
              <button
                key={s.handle}
                type="button"
                className={cn(styles.card, active && styles.cardActive)}
                aria-pressed={active}
                onClick={() => {
                  onPick(s)
                }}
              >
                <span className={styles.cardName}>{s.name}</span>
                <span className={styles.cardHandle}>
                  @{s.handle} <StatusBadge status={s.handle_status} />
                </span>
                {s.rationale ? <span className={styles.cardRationale}>{s.rationale}</span> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Handle availability
// ---------------------------------------------------------------------------

interface HandleStatusLineProps {
  tag: string
  name: string
  description: string
  onPickHandle: (handle: string) => void
}

export function HandleStatusLine({ tag, name, description, onPickHandle }: HandleStatusLineProps) {
  const handle = useDebounced(normalizeHandle(tag), 500)
  const valid = HANDLE_RE.test(handle)
  const status = useHandleStatus(handle, valid)
  const alternatives = useHandleAlternatives()
  // Every handle seen taken in this dialog — the next round of options skips them.
  const [taken, setTaken] = useState<string[]>([])

  const result: HandleStatus | null = !handle ? null : !valid ? 'invalid' : (status.data?.status ?? null)
  useEffect(() => {
    if (result === 'taken') setTaken((prev) => (prev.includes(handle) ? prev : [...prev, handle]))
  }, [result, handle])

  // A new handle makes the old options stale.
  const { reset } = alternatives
  useEffect(() => {
    reset()
  }, [handle, reset])

  if (!handle) return null

  const typing = normalizeHandle(tag) !== handle

  return (
    <div className={styles.status}>
      {typing || status.isFetching ? (
        <span className={styles.muted}>
          <Loader2 className={styles.spin} aria-hidden /> Conferindo @{normalizeHandle(tag)} no YouTube…
        </span>
      ) : result === 'invalid' ? (
        <span className={styles.error}>
          Formato inválido: 3 a 30 caracteres, só letras minúsculas sem acento, números, ponto, _ e -.
        </span>
      ) : result === 'available' ? (
        <span className={styles.ok}>
          <CheckCircle2 aria-hidden /> @{handle} está livre no YouTube.
        </span>
      ) : result === 'taken' ? (
        <span className={styles.error}>
          <XCircle aria-hidden /> @{handle} já está em uso no YouTube.
        </span>
      ) : status.isError || result === 'unknown' ? (
        <span className={styles.muted}>Não deu para conferir agora — o YouTube confirma na criação.</span>
      ) : null}

      {result === 'taken' && !typing ? (
        <div className={styles.row}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            leadingIcon={alternatives.isPending ? <Loader2 className={styles.spin} /> : <Sparkles />}
            disabled={alternatives.isPending || !name.trim()}
            onClick={() => {
              alternatives.mutate({
                name: name.trim(),
                description: description.trim(),
                exclude: taken.includes(handle) ? taken : [...taken, handle],
              })
            }}
          >
            {alternatives.isPending ? 'Procurando @ livres…' : 'Gerar opções livres'}
          </Button>
          <span className={styles.hint}>
            {name.trim()
              ? 'Se for o canal que você acabou de criar lá, pode salvar assim mesmo.'
              : 'Preencha o nome antes.'}
          </span>
          {alternatives.isError ? (
            <span className={styles.error}>{errorMessage(alternatives.error, 'Falha ao gerar opções.')}</span>
          ) : null}
        </div>
      ) : null}

      {alternatives.data ? (
        alternatives.data.length ? (
          <div className={styles.chips}>
            {alternatives.data.map((a: HandleCheck) => (
              <button
                key={a.handle}
                type="button"
                className={styles.chip}
                onClick={() => {
                  onPickHandle(a.handle)
                }}
              >
                @{a.handle}
                {a.status === 'available' ? <CheckCircle2 aria-hidden /> : null}
              </button>
            ))}
          </div>
        ) : (
          <span className={styles.muted}>Nenhuma opção livre — tente mudar o nome.</span>
        )
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

interface AvatarPanelProps {
  name: string
  description: string
  brief: AvatarBrief | null
  onBrief: (brief: AvatarBrief) => void
}

export function AvatarPanel({ name, description, brief, onBrief }: AvatarPanelProps) {
  const generateImage = useGenerateImage()
  const writeBrief = useAvatarPrompt()
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)

  // The blob URL dies with the dialog — same pattern as ImageGenerationPage.
  const urlRef = useRef<string | null>(null)
  urlRef.current = avatarUrl
  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    }
  }, [])

  const trimmed = description.trim()
  // The brief is stale once the description moves on from the text it was
  // written from — regenerating then asks for a new brief first.
  const stale = !brief || brief.description !== trimmed
  const pending = writeBrief.isPending || generateImage.isPending

  function paint(prompt: string) {
    generateImage.mutate(
      // Pollinations, not HF: on 21/10/2026 every HF route for FLUX.1-schnell
      // refused (nscale 402 for credits, fal-ai/wavespeed don't serve it),
      // and the old Channel Creator tool had no successful generation in the
      // logs either. Pollinations answered in 1–30 s from the skab.
      { prompt, provider: 'pollinations', style: 'photo', aspectRatio: 'avatar', seed: null },
      {
        onSuccess: ({ url }) => {
          if (urlRef.current) URL.revokeObjectURL(urlRef.current)
          setAvatarUrl(url)
        },
      },
    )
  }

  function run() {
    if (pending) return
    if (brief && !stale) {
      paint(brief.prompt)
      return
    }
    if (trimmed.length < MIN_DESCRIPTION) return
    writeBrief.mutate(
      { name: name.trim(), description: trimmed },
      {
        onSuccess: (prompt) => {
          onBrief({ prompt, description: trimmed })
          paint(prompt)
        },
      },
    )
  }

  const err = writeBrief.isError
    ? errorMessage(writeBrief.error, 'Falha ao gerar o briefing.')
    : generateImage.isError
      ? // On 21/10/2026 the backend's 502 (every HF route refused) reached the
        // browser as a bare TypeError ("Failed to fetch"), with no readable
        // body — say something the owner can act on instead. ``authFetch``
        // now turns that rejection into an ``ApiError`` of kind ``network``.
        errorKind(generateImage.error) === 'network'
        ? 'O gerador de imagem não respondeu. Tente de novo em alguns segundos.'
        : errorMessage(generateImage.error, 'Falha ao gerar a foto.')
      : null

  return (
    <div className={styles.panel}>
      <span className={styles.label}>Foto do canal</span>
      <div className={styles.avatarRow}>
        <div className={styles.avatarBox}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Foto gerada para o canal" className={styles.avatarImage} />
          ) : pending ? (
            <Loader2 className={styles.spin} aria-hidden />
          ) : (
            <UserRoundPlus aria-hidden />
          )}
        </div>
        <div className={styles.avatarMeta}>
          <div className={styles.row}>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              leadingIcon={avatarUrl ? <RotateCw /> : <Sparkles />}
              onClick={run}
              disabled={pending || (stale && trimmed.length < MIN_DESCRIPTION)}
            >
              {writeBrief.isPending
                ? 'Escrevendo o briefing…'
                : generateImage.isPending
                  ? 'Gerando a foto…'
                  : avatarUrl
                    ? 'Regenerar foto'
                    : 'Gerar foto'}
            </Button>
            {avatarUrl ? (
              <a href={avatarUrl} download="prism-avatar.png" className={styles.download}>
                <Download aria-hidden /> Baixar
              </a>
            ) : null}
          </div>
          <span className={styles.hint}>
            {avatarUrl && stale
              ? 'A descrição mudou: regenerar reescreve só a foto, nome e @ ficam.'
              : 'Baixe e suba no YouTube (Studio → Personalização → Imagem). Depois de conectar o canal, a foto de lá aparece no Prism.'}
          </span>
          {brief ? (
            <details className={styles.brief}>
              <summary>Briefing da foto</summary>
              <p>{brief.prompt}</p>
            </details>
          ) : null}
          {err ? <span className={styles.error}>{err}</span> : null}
        </div>
      </div>
    </div>
  )
}
